/**
 * Option grids of the profile editor: native radio groups (keyboard and screen-reader
 * friendly) rendered as big tappable tiles.
 */
import {
  AVATARS,
  type AvatarId,
  COLORS,
  type ColorId,
  ICONS,
  type IconId,
  type PlayerSkin,
  TRAILS,
  type TrailId,
} from '@cube-rush/shared';
import type { ComponentChildren } from 'preact';
import { useId } from 'preact/hooks';
import { ru } from '../../i18n/ru';
import { CubePreview } from '../components/CubePreview';
import { TrailSample } from '../skin/TrailSample';
import './pickers.css';

interface PickProps {
  group: string;
  selected: boolean;
  label: string;
  onSelect(): void;
  children: ComponentChildren;
  class?: string;
}

function Pick({ group, selected, label, onSelect, children, class: className }: PickProps) {
  return (
    <label class={`pick${selected ? ' pick--on' : ''}${className ? ` ${className}` : ''}`}>
      <input
        type="radio"
        class="visually-hidden pick__input"
        name={group}
        checked={selected}
        aria-label={label}
        onChange={onSelect}
      />
      {children}
    </label>
  );
}

function PickGroup({
  title,
  showTitle = false,
  variant,
  children,
}: {
  title: string;
  /** Show the title above the grid (otherwise only screen readers announce it). */
  showTitle?: boolean;
  variant: 'avatars' | 'icons' | 'colors' | 'trails';
  children: ComponentChildren;
}) {
  return (
    <fieldset class={`pick-grid pick-grid--${variant}`}>
      <legend class={showTitle ? 'pick-grid__title' : 'visually-hidden'}>{title}</legend>
      {children}
    </fieldset>
  );
}

export function AvatarPicker({
  value,
  onChange,
}: {
  value: AvatarId;
  onChange(id: AvatarId): void;
}) {
  const group = useId();
  return (
    <PickGroup title={ru.profile.tabs.avatar} variant="avatars">
      {AVATARS.map((avatar) => (
        <Pick
          key={avatar.id}
          group={group}
          selected={avatar.id === value}
          label={avatar.emoji}
          onSelect={() => onChange(avatar.id)}
          class="pick--emoji"
        >
          <span aria-hidden="true">{avatar.emoji}</span>
        </Pick>
      ))}
    </PickGroup>
  );
}

export function IconPicker({ skin, onChange }: { skin: PlayerSkin; onChange(id: IconId): void }) {
  const group = useId();
  return (
    <PickGroup title={ru.profile.tabs.icon} variant="icons">
      {ICONS.map((icon) => (
        <Pick
          key={icon.id}
          group={group}
          selected={icon.id === skin.icon}
          label={icon.name}
          onSelect={() => onChange(icon.id)}
          class="pick--tile"
        >
          <CubePreview look={{ ...skin, icon: icon.id }} size={40} />
          <span class="pick__caption" aria-hidden="true">
            {icon.name}
          </span>
        </Pick>
      ))}
    </PickGroup>
  );
}

export function ColorPicker({
  title,
  value,
  onChange,
}: {
  title: string;
  value: ColorId;
  onChange(id: ColorId): void;
}) {
  const group = useId();
  return (
    <div class="color-picker">
      <PickGroup title={title} showTitle variant="colors">
        {COLORS.map((color) => (
          <Pick
            key={color.id}
            group={group}
            selected={color.id === value}
            label={ru.profile.colorNames[color.id]}
            onSelect={() => onChange(color.id)}
            class="pick--swatch"
          >
            <span class="pick__swatch" style={{ background: color.hex }} />
          </Pick>
        ))}
      </PickGroup>
    </div>
  );
}

export function TrailPicker({ skin, onChange }: { skin: PlayerSkin; onChange(id: TrailId): void }) {
  const group = useId();
  return (
    <PickGroup title={ru.profile.tabs.trail} variant="trails">
      {TRAILS.map((trail) => (
        <Pick
          key={trail.id}
          group={group}
          selected={trail.id === skin.trail}
          label={trail.name}
          onSelect={() => onChange(trail.id)}
          class="pick--tile"
        >
          <TrailSample skin={skin} trail={trail.id} />
          <span class="pick__caption" aria-hidden="true">
            {trail.name}
          </span>
        </Pick>
      ))}
    </PickGroup>
  );
}
